/**
 * Open-maintenance summary card — compact, mobile-friendly open-entries list
 * on the site dashboard.
 *
 * Reads from the shared maintenanceLogs slice (lazy-loaded on mount, same
 * fetch the maintenance blade and rail issue card use) and shows only entries
 * with no resolution date. Row click opens the focused entry in the
 * maintenance blade; the full history (open and resolved, all entries) lives
 * at the routed Maintenance History page instead of an embedded grid.
 *
 * Contains:
 * - MaintenanceSummaryCard — compact open-maintenance list card (default export)
 */
import AddIcon from '@mui/icons-material/Add';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Button, ButtonBase, Card, IconButton, Tooltip, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { loadMaintenanceLogs, selectMaintenanceLogs } from '@sophocles/features/entities/site';
import { formatDate, formatDuration, userRefName } from '@sophocles/utils/formatUtils';

// Caps the card's own scroll region so a site with many open entries doesn't
// push the rest of the dashboard down; the list scrolls internally past this.
const LIST_MAX_HEIGHT_PX = 340;

function byNewestReported(a, b) {
  return (b.date_reported ?? '').localeCompare(a.date_reported ?? '');
}

/**
 * @param {object}   props
 * @param {object}   props.log     - open maintenance log entry ({ id, issue, date_reported, entered_by_user })
 * @param {object}   props.user    - viewing user, for date formatting
 * @param {Function} props.onOpen  - fn(id) — opens the entry in the maintenance blade
 */
function MaintenanceSummaryRow({ log, user, onOpen }) {
  const { t } = useTranslation();
  const reportedMs = log.date_reported ? Date.parse(log.date_reported) : NaN;
  const openForMs = Number.isNaN(reportedMs) ? null : Date.now() - reportedMs;
  const reporter = userRefName(log.entered_by_user ?? null);

  return (
    <ButtonBase
      onClick={() => onOpen(log.id)}
      sx={{
        display: 'flex',
        width: '100%',
        textAlign: 'left',
        gap: 'var(--space-2)',
        py: 'var(--space-2)',
        px: 'var(--space-3)',
        borderTop: '1px solid var(--md-outline-variant)',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {log.issue}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: '2px' }}>
          {[formatDate(log.date_reported, user), reporter].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }} noWrap>
          {t('maintenance.openFor', 'Open {{duration}}', { duration: formatDuration(openForMs) })}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

MaintenanceSummaryRow.propTypes = {
  log: PropTypes.object.isRequired,
  user: PropTypes.object,
  onOpen: PropTypes.func.isRequired,
};

/**
 * @param {object}        props
 * @param {number|string} props.siteId      - site id used for the log load + selector
 * @param {Function}      props.onOpen      - opens the Maintenance blade for a given log id
 * @param {Function}      props.onAdd       - opens the Maintenance blade in add mode
 * @param {object}        props.capabilities - from capabilities slice
 * @param {string}        props.historyPath  - router path for the "View history" header action
 */
function MaintenanceSummaryCard({ siteId, onOpen, onAdd, capabilities, historyPath }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useUser();

  const logs = useSelector(selectMaintenanceLogs(siteId));
  const canAdd = capabilities?.can_add_maintenance ?? false;

  // Not part of the composite dashboard snapshot — lazy-load the same way the
  // rail's issue card and the maintenance blade do.
  useEffect(() => {
    if (siteId != null) dispatch(loadMaintenanceLogs(siteId));
  }, [dispatch, siteId]);

  const openLogs = useMemo(
    () =>
      logs
        .filter(log => log.date_resolved == null)
        .slice()
        .sort(byNewestReported),
    [logs],
  );

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          // 12px header padding matches the relay card header so the header
          // controls land at the same offset on every dashboard card.
          px: 'var(--space-3)',
          py: 'var(--space-3)',
          flexShrink: 0,
        }}
      >
        <BuildOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
          {t('sites.maintenanceLog')}
        </Typography>
        {canAdd && (
          <Tooltip title={t('maintenance.addEntry', 'Add entry')}>
            <IconButton size="small" aria-label={t('maintenance.addEntry', 'Add entry')} onClick={onAdd}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button size="small" endIcon={<ChevronRightIcon fontSize="small" />} onClick={() => navigate(historyPath)}>
          {t('general.history', 'History')}
        </Button>
      </Box>

      {openLogs.length > 0 ? (
        <Box sx={{ maxHeight: LIST_MAX_HEIGHT_PX, overflowY: 'auto' }}>
          {openLogs.map(log => (
            <MaintenanceSummaryRow key={log.id} log={log} user={user} onOpen={onOpen} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            px: 'var(--space-3)',
            py: 'var(--space-3)',
            borderTop: '1px solid var(--md-outline-variant)',
          }}
        >
          <TaskAltIcon sx={{ fontSize: 18, color: green[500], flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('maintenance.noOpenEntries', 'No open entries')}
          </Typography>
        </Box>
      )}
    </Card>
  );
}

MaintenanceSummaryCard.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onOpen: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  capabilities: PropTypes.object,
  historyPath: PropTypes.string.isRequired,
};

export default MaintenanceSummaryCard;
